import React, { useState, useEffect, Suspense, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearAuth } from '../../redux/slices/authSlice';
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  useTheme,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Warning as WarningIcon,
  OpenInFull as OpenInFullIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useUserData } from '../../hooks/useUserData';

import { useLocale } from '../../context/LocaleContext';
import NotificationBell from '../notifications/NotificationBell';
import FacilityFilter from '../facilities/FacilityFilter';
import { clearFacilities } from '../../redux/slices/facilitiesSlice';
import { clearPatients } from '../../redux/slices/patientsSlice';
import { clearLogs } from '../../redux/slices/auditSlice';
import requestDeduplicationService from '../../services/requestDeduplicationService';
import AutoClockInManager from '../auth/AutoClockInManager';
import AIChatWidget from '../ai/AIChatWidget';
import { useClockInStatus } from '../../hooks/useClockInStatus';
import { useTriageVisibility } from '../../hooks/useTriageVisibility';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { useFacilityRedirect } from '../../hooks/useFacilityRedirect';
import { useNavigationTracking } from '../../hooks/useNavigationTracking';


// Custom hooks
import { useDrawerState } from './hooks/useDrawerState';
import { useNavigationHistory } from './hooks/useNavigationHistory';
import { useUserProfile } from './hooks/useUserProfile';
import { useFacilityBranding } from './hooks/useFacilityBranding';
import { useKioskMode } from './hooks/useKioskMode';
import { ProfilePictureProvider } from '../../context/ProfilePictureContext';


// Components
import LayoutDrawer from './components/LayoutDrawer';
import NavigationBreadcrumbs from './components/NavigationBreadcrumbs';
import UserProfileMenu from './components/UserProfileMenu';

// Utils and constants
import { createNavigationLinks } from './navigationConfig';
import { ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN } from './constants';




const Layout = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [primaryFacilityId, setPrimaryFacilityId] = useState(null);
  
  // Ref to prevent excessive facility setting
  const isSettingPrimaryFacility = useRef(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  const { user, loading } = useAuth();
  const { user: userData } = useUserData();
  const userFacilities = useSelector(state => state.facilities.userFacilities);
  
  // Consider facilities loaded when user has defaultFacility or userFacilities
  const facilitiesLoaded = !!userData?.defaultFacility || (userData && Object.keys(userData).length > 0);
  
  const { t, isRTL, isInitialized } = useLocale();
  
  const { logout } = useAuth();
  
  // Memoize userFacilities to prevent unnecessary re-renders - only change when content actually changes
  const memoizedUserFacilities = useMemo(() => {
    if (!userFacilities || !Array.isArray(userFacilities)) return [];
    return userFacilities;
  }, [userFacilities]);
  
  // Custom hooks
  const drawerState = useDrawerState(isRTL);
  const navigationHistoryHook = useNavigationHistory(userData); // Use userData instead of user
  const userProfile = useUserProfile(user, location);
  const { branding } = useFacilityBranding(user);
  const { isKioskMode, handleKioskMode } = useKioskMode();

  // Check clock-in status for the primary facility
  const shouldCheckClockStatus = !loading && facilitiesLoaded && primaryFacilityId;
  const { isClockedIn, loading: clockStatusLoading, requiresClockIn, forceRefresh } = useClockInStatus(
    shouldCheckClockStatus ? primaryFacilityId : null
  );

  // Debug logging for clock status changes
  useEffect(() => {
    console.log('🏥 Layout: Clock status updated:', {
      isClockedIn,
      requiresClockIn,
      clockStatusLoading,
      primaryFacilityId
    });
  }, [isClockedIn, requiresClockIn, clockStatusLoading, primaryFacilityId]);

  // Listen for additional clock status events to force refresh if needed
  useEffect(() => {
    const handleClockStatusChange = (event) => {
      console.log('🏥 Layout: Received clock status change event:', event.detail);
      // Force a refresh to ensure we have the latest status
      if (forceRefresh) {
        setTimeout(() => {
          forceRefresh();
        }, 200);
      }
    };

    window.addEventListener('clockStatusUpdate', handleClockStatusChange);
    return () => {
      window.removeEventListener('clockStatusUpdate', handleClockStatusChange);
    };
  }, [forceRefresh]);

  // Check if Triage Board should be visible
  const { shouldShowTriage, loading: triageVisibilityLoading } = useTriageVisibility();

  // Fetch primary facility from Redux state
  const fetchPrimaryFacility = useCallback(async () => {
    // Prevent concurrent executions
    if (isSettingPrimaryFacility.current || memoizedUserFacilities.length === 0) {
      return;
    }
    
    isSettingPrimaryFacility.current = true;
    
    try {
      let primaryId = null;
      
      if (user?.defaultFacility && memoizedUserFacilities.length > 0) {
        // defaultFacility is now the complete facility object, extract the ID
        const defaultFacilityId = typeof user.defaultFacility === 'object' ? user.defaultFacility.id : user.defaultFacility;
        const defaultFacility = memoizedUserFacilities.find(f => f.id === defaultFacilityId);
        if (defaultFacility) {
          primaryId = defaultFacilityId;
        }
      }
      
      // If no default facility or default not found, use first available
      if (!primaryId && memoizedUserFacilities.length > 0) {
        primaryId = memoizedUserFacilities[0].id;
      }
      
      // Only update if the primary facility ID has actually changed
      if (primaryId && primaryId !== primaryFacilityId) {
        setPrimaryFacilityId(primaryId);
        console.log('Primary facility set in Layout:', primaryId);
      }
    } finally {
      isSettingPrimaryFacility.current = false;
    }
  }, [user?.defaultFacility, memoizedUserFacilities, primaryFacilityId]);



  // Listen for facility changes and trigger appropriate refreshes
  const handleFacilityChange = useCallback((newFacilityId, previousFacilityId) => {
    // Only update if the facility actually changed and we have facilities loaded
    if (newFacilityId !== previousFacilityId && memoizedUserFacilities.length > 0) {
      if (newFacilityId !== primaryFacilityId) {
        fetchPrimaryFacility();
      }
      userProfile.fetchDefaultFacilityName(memoizedUserFacilities);
    }
  }, [fetchPrimaryFacility, userProfile, memoizedUserFacilities, primaryFacilityId]);

  useFacilityChangeListener(handleFacilityChange);

  // Handle facility redirects for patient/encounter details pages
  useFacilityRedirect();

  // Track navigation events to prevent session clearing during internal navigation
  useNavigationTracking();

  // Set primary facility when login is complete
  useEffect(() => {
    if (!loading && facilitiesLoaded && user?.uid && memoizedUserFacilities.length > 0) {
      // Only fetch primary facility if we don't already have one set or if facilities have changed
      if (!primaryFacilityId) {
        fetchPrimaryFacility();
      }
    }
  }, [loading, facilitiesLoaded, user?.uid, memoizedUserFacilities.length, primaryFacilityId, fetchPrimaryFacility]);

  // Fetch default facility name when user or facilities change
  useEffect(() => {
    if (facilitiesLoaded && memoizedUserFacilities.length > 0) {
      userProfile.fetchDefaultFacilityName(memoizedUserFacilities);
    }
  }, [facilitiesLoaded, memoizedUserFacilities, userProfile]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    dispatch(clearAuth());
    dispatch(clearFacilities());
    dispatch(clearPatients());
    dispatch(clearLogs());
    requestDeduplicationService.clearAllCaches();
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    // Check if user needs to be clocked in for this path
    if (requiresClockIn && !isClockedIn && !ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN.includes(path)) {
      console.log('Navigation blocked: User needs to clock in first');
      return;
    }
    
    navigate(path);
    if (drawerState.isMobile) {
      drawerState.handleDrawerToggle();
    }
    
    // Track navigation for breadcrumbs (local session only)
    if (navigationHistoryHook.updateNavigationHistory) {
      navigationHistoryHook.updateNavigationHistory(path);
    }
  };




  // Note: Removed isParentSelected function since it was unused after removing auto-expand logic

  const userRoles = user?.roles || [user?.role].filter(Boolean);
  
  const navLinks = useMemo(() => {
    if (!isInitialized || triageVisibilityLoading || !user) return [];
    return createNavigationLinks(t, shouldShowTriage).filter(link => {
      if (!link.roles) return true;
      // Check if any of the user's roles match the link's allowed roles
      return userRoles.some(userRole => link.roles.includes(userRole));
    });
  }, [isInitialized, triageVisibilityLoading, t, shouldShowTriage, userRoles, user]);

  // Note: Removed auto-expand logic since openCollapse state was unused






  useEffect(() => {
    document.body.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: 'white', color: 'black' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={drawerState.handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Desktop collapse button */}
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={drawerState.handleDrawerCollapse}
            sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Navigation History Breadcrumb */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1,
            ml: 2,
            overflow: 'hidden'
          }}>
            {/* Show navigation breadcrumb only if clocked in or clock-in not required */}
                         {(!requiresClockIn || isClockedIn) && (
               <NavigationBreadcrumbs
                 navigationHistory={navigationHistoryHook.navigationHistory}
                 pinnedItems={navigationHistoryHook.pinnedItems}
                 location={location}
                 handleNavigation={handleNavigation}
                 handleTogglePin={navigationHistoryHook.handleTogglePin}
                 navLinks={navLinks}
               />
             )}
            
            {/* Clock-in required message */}
            {requiresClockIn && !isClockedIn && !clockStatusLoading && (
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                backgroundColor: 'warning.light',
                color: 'warning.contrastText',
                borderRadius: 1,
                fontSize: '0.875rem'
              }}>
                <WarningIcon fontSize="small" />
                <Typography variant="body2">
                  Please clock in to access the EMR system
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Kiosk Mode Button */}
          <Tooltip title="Kiosk Mode">
            <IconButton
              onClick={handleKioskMode}
              sx={{
                mr: 1,
                color: isKioskMode ? 'primary.main' : 'text.primary',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <OpenInFullIcon 
                sx={{
                  transform: isKioskMode ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </IconButton>
          </Tooltip>
          
          <FacilityFilter />
          <NotificationBell />
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar 
              src={loading ? '' : userProfile.userProfilePicture}
              sx={{ 
                bgcolor: branding.primaryColor,
                width: 40,
                height: 40
              }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : !userProfile.userProfilePicture ? (
                user?.name ? user.name.charAt(0).toUpperCase() : <AccountCircleIcon />
              ) : null}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <UserProfileMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        user={user}
        userProfilePicture={userProfile.userProfilePicture}
        defaultFacilityName={userProfile.defaultFacilityName}
        branding={branding}
        loading={loading}
        t={t}
        onNavigateToAccount={() => handleNavigation('/my-account')}
        onLogout={handleLogout}
      />

      <LayoutDrawer
        mobileOpen={drawerState.mobileOpen}
        handleDrawerToggle={drawerState.handleDrawerToggle}
        currentDrawerWidth={drawerState.currentDrawerWidth}
        isDrawerCollapsed={drawerState.isDrawerCollapsed}
        handleDrawerCollapse={drawerState.handleDrawerCollapse}
        branding={branding}
        t={t}
        shouldShowTriage={shouldShowTriage}
        userRoles={user?.roles || [user?.role].filter(Boolean)}
        requiresClockIn={requiresClockIn}
        isClockedIn={isClockedIn}
        clockStatusLoading={clockStatusLoading}
        handleNavigation={handleNavigation}
        location={location}
        isMobile={drawerState.isMobile}
        isRTL={isRTL}
        theme={theme}
        isResizing={drawerState.isResizing}
        handleMouseDown={drawerState.handleMouseDown}
        resizeRef={drawerState.resizeRef}
      />

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          pt: 3,
          pb: 3,
          pl: { xs: 2, md: 1 },
          pr: { xs: 2, md: 1 },
          transition: theme.transitions.create(['padding'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Suspense fallback={<CircularProgress />}>
          <ProfilePictureProvider refreshProfilePicture={userProfile.refreshProfilePicture}>
            <Outlet />
          </ProfilePictureProvider>
        </Suspense>
      </Box>
      
      {/* Auto Clock-In Manager - runs in background to check for shift schedules */}
      <AutoClockInManager />
      
      {/* AI Chat Assistant */}
      <AIChatWidget />

    </Box>
  );
};

export default Layout;